import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { isStandalone as checkIsStandalone } from '../utils/pwa-environment';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallContextValue {
  canInstall: boolean;
  isStandalone: boolean;
  deferredPrompt: BeforeInstallPromptEvent | null;
  promptInstall: () => Promise<boolean>;
}

const PWAInstallContext = createContext<PWAInstallContextValue | null>(null);

export function PWAInstallProvider({ children }: { children: React.ReactNode }) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    setIsStandalone(checkIsStandalone());
  }, []);

  useEffect(() => {
    const stored = (window as Window & { __deferredInstallPrompt?: BeforeInstallPromptEvent })
      .__deferredInstallPrompt;
    if (stored) setDeferredPrompt(stored);

    const handler = (e: Event) => {
      e.preventDefault();
      const ev = e as BeforeInstallPromptEvent;
      (window as Window & { __deferredInstallPrompt?: BeforeInstallPromptEvent }).__deferredInstallPrompt = ev;
      setDeferredPrompt(ev);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setDeferredPrompt(null);
    return outcome === 'accepted';
  }, [deferredPrompt]);

  const value: PWAInstallContextValue = {
    canInstall: !isStandalone && deferredPrompt !== null,
    isStandalone,
    deferredPrompt,
    promptInstall,
  };

  return (
    <PWAInstallContext.Provider value={value}>{children}</PWAInstallContext.Provider>
  );
}

export function usePWAInstallContext(): PWAInstallContextValue | null {
  return useContext(PWAInstallContext);
}
